
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { TbRefresh } from "react-icons/tb";
import {
  Paper,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { BaseUrl } from "../../../../../../api/AxiosApi";
import DownloadJpegButton from "../../../../../../components/downloadJpegButton/DownloadJpegButton";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function getDateTimeForDashboardTab1(date = new Date()) {
  const formattedCurrentDate = date.toISOString().split("T")[0];
  const oneWeekAgo = new Date(date);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const formattedOneWeekAgo = oneWeekAgo.toISOString().split("T")[0];
  return {
    Today: formattedCurrentDate,
    LastWeek: formattedOneWeekAgo,
  };
}

function InvCufPerformance() {
  const chartRef = useRef();
  const [inverters, setInverters] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [fromDate, setFromDate] = useState(getDateTimeForDashboardTab1().LastWeek);
  const [toDate, setToDate] = useState(getDateTimeForDashboardTab1().Today);
  const [metric, setMetric] = useState("CUF");
  const [idealValue, setIdealValue] = useState(20);
  const [loading, setLoading] = useState(false);

  const credentials = {
    LOGIN_ID: localStorage.getItem("LoginID"),
    LOC_CODE: localStorage.getItem("LCODE"),
    PASS_KEY: "XYZ",
    WEB_KEY: "WEBXYZ321",
    TYP: "I",
  };

  // Fetch inverters and select first by default
  useEffect(() => {
    async function fetchInverters() {
      try {
        const res = await axios.get(`${BaseUrl}CMN_07_Super/GET_FEEDER_LIST`, {
          params: credentials,
        });
        const feederList = res.data.FEED || [];
        setInverters(feederList);
        // Select first inverter ID by default
        if (feederList.length > 0) {
          setSelectedKeys([feederList[0].feeder_id]);
        }
        setChartData(null);
      } catch (err) {
        console.error("Inverter API Error:", err);
      }
    }
    fetchInverters();
  }, []);

  // Fetch chart data on refresh button click
  const fetchChartData = async () => {
    if (selectedKeys.length === 0) {
      setChartData(null);
      return;
    }
    setLoading(true);

    let labels = [];
    let datasets = [];

    // Always add Ideal Value dataset by default (no checkbox)
    let idealLabels = [];
    if (inverters.length > 0) {
      try {
        const res = await axios.get(`${BaseUrl}CMN_08InvLog/Get_Inv_Perf`, {
          params: {
            ...credentials,
            MTR_NO: inverters[0].feeder_id,
            DT1: fromDate,
            DT2: toDate,  
          },
        });
        const data = res.data.data || [];
        idealLabels = data.map((d) => d.DATET);
      } catch {
        idealLabels = [];
      }
    }
    labels = idealLabels.length ? idealLabels : [fromDate, toDate];
    datasets.push({
      label: "Ideal Value",
      data: labels.map(() => Number(idealValue)),
      borderColor: "red",
      borderDash: [8, 4],
      fill: false,
      tension: 0,
      borderWidth: 2,
      pointRadius: 0,
    });

    for (const inv of inverters) {
      if (!selectedKeys.includes(inv.feeder_id)) continue;

      try {
        const res = await axios.get(`${BaseUrl}CMN_08InvLog/Get_Inv_Perf`, {
          params: {
            ...credentials,
            MTR_NO: inv.feeder_id,
            DT1: fromDate,
            DT2: toDate,
          },
        });
        const apiData = res.data.data || [];
        if (!labels.length) {
          labels = apiData.map((d) => d.DATET);
        }
        datasets.push({
          label: inv.name,
          data: apiData.map((d) => parseFloat(d[metric]) || 0),
          borderColor: "#" + Math.floor(Math.random() * 16777215).toString(16),
          fill: false,
          tension: 0.3,
        });
      } catch {
        // Handle errors silently
      }
    }

    setChartData({ labels, datasets });
    setLoading(false);
  };

  const handleToggle = (key) =>
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  // 🔁 Auto refresh chart when Ideal Value changes
  useEffect(() => {
    if (chartData) {
      fetchChartData(); // Refresh the chart automatically
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idealValue]);


  return (
    <div style={{ padding: "10px" }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          Inverter Wise CUF Comparison
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: "white" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            mb: 1,
            fontSize: "0.75rem",
          }}
        >
          {inverters.map((inv) => (
            <FormControlLabel
              key={inv.feeder_id}
              control={
                <Checkbox
                  checked={selectedKeys.includes(inv.feeder_id)}
                  onChange={() => handleToggle(inv.feeder_id)}
                  color="primary"
                  sx={{
                    p: 0.3,                // ✅ less padding
                    transform: "scale(0.7)", // ✅ shrink size to 80%
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  <Typography variant="body2">{inv.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    [{inv.kwp} kWp]
                  </Typography>
                </Box>
              }
            />
          ))}
        </Box>
      </Paper>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          mb: 1,
        }}
      >
        <label style={{ fontSize: 14, fontWeight: 500 }}>Ideal Value:</label>
        <TextField
          size="small"
          type="number"
          sx={{ width: 70, fontSize: "0.75rem" }}
          value={idealValue}
          onChange={(e) => setIdealValue(e.target.value)}
        />

        <label style={{ fontSize: 14, fontWeight: 500 }}>From:</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{ height: 32, borderRadius: ".3rem" }}
        />
        <label style={{ fontSize: 14, fontWeight: 500 }}>To:</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{ height: 32, borderRadius: ".3rem" }}
        />&nbsp;

        <TbRefresh
          style={{ fontSize: "1.5rem", cursor: "pointer" }}
          onClick={fetchChartData}
          title="Refresh Chart"
        />

        <DownloadJpegButton targetRef={chartRef} fileName={`Inverter Wise CUF Comparison.jpeg`} />
      </Box>

      <Paper ref={chartRef} sx={{ p: 2, bgcolor: "white", height: 350 }}>
        {loading ? (
          <Typography sx={{ textAlign: "center", mt: 12, color: "#999" }}>
            Loading...
          </Typography>
        ) : chartData && chartData.labels.length > 0 && chartData.datasets.length > 0 ? (
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "top", labels: { font: { size: 13 } } },
              },
              scales: {
                x: { ticks: { font: { size: 11 } } },
                y: { ticks: { font: { size: 11 } } },
              },
            }}
          />
        ) : (
          <Typography sx={{ textAlign: "center", mt: 12, color: "#bbb" }}>
            No data to display.
          </Typography>
        )}
      </Paper>
    </div>
  );
}

export default InvCufPerformance;





